import { createApi } from "@reduxjs/toolkit/query/react";
import type { FetchBaseQueryError } from "@reduxjs/toolkit/query/react";
import { createBaseQuery } from "@/api/base-query";
import { API_ENDPOINTS } from "@/api/config";
import type {
  GetRoutingPublicFormInput,
  RoutingPublicField,
  RoutingPublicForm,
  RoutingPublicFormResponse,
  RoutingPublicSubmitData,
  RoutingPublicSubmitInput,
  RoutingPublicSubmitRequest,
  RoutingPublicSubmitResponse,
  RoutingPublicSubmitResult,
} from "./routing-public.types";
import { ROUTING_PUBLIC_VALIDATION_MESSAGES } from "./routing-public.types";

const ENDPOINTS = API_ENDPOINTS.scheduling.routingForms;
const publicBaseQuery = createBaseQuery("public");

const createApiError = (
  status: number,
  message: string,
): { error: FetchBaseQueryError } => ({
  error: {
    status,
    data: { message },
  },
});

const trimString = (value: unknown): string =>
  typeof value === "string" ? value.trim() : "";

const isPlainRecord = (value: unknown): value is Record<string, unknown> =>
  Object.prototype.toString.call(value) === "[object Object]";

const isPositiveInteger = (value: unknown): value is number =>
  typeof value === "number" && Number.isInteger(value) && value > 0;

const isNonNegativeInteger = (value: unknown): value is number =>
  typeof value === "number" && Number.isInteger(value) && value >= 0;

const isValidEmail = (value: string): boolean =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

const extractEnvelopeMessage = (value: unknown, fallback: string): string => {
  if (isPlainRecord(value)) {
    const message = value.message;
    if (typeof message === "string" && message.trim()) return message;
  }
  return fallback;
};

const normalizeFormInput = (
  input: GetRoutingPublicFormInput,
): { publicKey: string } => {
  if (typeof input === "string") return { publicKey: trimString(input) };
  return { publicKey: trimString(input.publicKey) };
};

const validatePublicKey = (publicKey: string): string | null => {
  if (!publicKey) return ROUTING_PUBLIC_VALIDATION_MESSAGES.PUBLIC_KEY_REQUIRED;
  if (!/^rf_[A-Za-z0-9_-]+$/.test(publicKey)) {
    return ROUTING_PUBLIC_VALIDATION_MESSAGES.INVALID_PUBLIC_KEY;
  }
  return null;
};

const isJsonValue = (value: unknown): boolean => {
  if (
    value === null ||
    typeof value === "string" ||
    typeof value === "boolean"
  ) {
    return true;
  }

  if (typeof value === "number") return Number.isFinite(value);
  if (Array.isArray(value)) return value.every(isJsonValue);
  if (!isPlainRecord(value)) return false;

  return Object.values(value).every(isJsonValue);
};

const isJsonRecord = (value: unknown): value is Record<string, unknown> =>
  isPlainRecord(value) && Object.values(value).every(isJsonValue);

const isRoutingPublicField = (value: unknown): value is RoutingPublicField => {
  if (!isPlainRecord(value)) return false;
  return (
    isPositiveInteger(value.id) &&
    typeof value.label === "string" &&
    typeof value.fieldKey === "string" &&
    typeof value.fieldType === "string" &&
    typeof value.isRequired === "boolean" &&
    isNonNegativeInteger(value.sortOrder) &&
    (value.options === undefined ||
      value.options === null ||
      isJsonRecord(value.options) ||
      (Array.isArray(value.options) && value.options.every(isJsonValue))) &&
    (value.validationRules === undefined ||
      value.validationRules === null ||
      isJsonRecord(value.validationRules))
  );
};

const isRoutingPublicForm = (value: unknown): value is RoutingPublicForm => {
  if (!isPlainRecord(value)) return false;
  return (
    isPositiveInteger(value.workspaceId) &&
    typeof value.name === "string" &&
    typeof value.slug === "string" &&
    typeof value.publicKey === "string" &&
    typeof value.title === "string" &&
    (value.settings === undefined ||
      value.settings === null ||
      isJsonRecord(value.settings)) &&
    Array.isArray(value.fields) &&
    value.fields.every(isRoutingPublicField)
  );
};

const isRoutingPublicSubmitData = (
  value: unknown,
): value is RoutingPublicSubmitData => {
  if (!value) return true;
  if (!isPlainRecord(value)) return false;

  return (
    optionalString(value.submissionReference) &&
    optionalInteger(value.submissionId) &&
    optionalInteger(value.matchedRuleId) &&
    optionalString(value.targetType) &&
    optionalInteger(value.routedEventTypeId) &&
    optionalString(value.bookingPublicKey) &&
    optionalString(value.bookingUrl) &&
    optionalInteger(value.targetUserId) &&
    optionalString(value.targetUrl)
  );
};

const optionalString = (value: unknown): boolean =>
  value === undefined || value === null || typeof value === "string";

const optionalInteger = (value: unknown): boolean =>
  value === undefined ||
  value === null ||
  (typeof value === "number" && Number.isInteger(value) && value >= 0);

const validateSubmitInput = (
  input: RoutingPublicSubmitInput,
): string | null => {
  const publicKeyError = validatePublicKey(trimString(input.publicKey));
  if (publicKeyError) return publicKeyError;

  const inviteeName = trimString(input.inviteeName);
  if (inviteeName.length > 180) {
    return ROUTING_PUBLIC_VALIDATION_MESSAGES.INVITEE_NAME_TOO_LONG;
  }

  const inviteeEmail = trimString(input.inviteeEmail);
  if (inviteeEmail && !isValidEmail(inviteeEmail)) {
    return ROUTING_PUBLIC_VALIDATION_MESSAGES.INVALID_INVITEE_EMAIL;
  }

  if (trimString(input.inviteePhone).length > 50) {
    return ROUTING_PUBLIC_VALIDATION_MESSAGES.INVITEE_PHONE_TOO_LONG;
  }

  if (input.answers === undefined || input.answers === null) {
    return ROUTING_PUBLIC_VALIDATION_MESSAGES.ANSWERS_REQUIRED;
  }
  if (!isJsonRecord(input.answers)) {
    return ROUTING_PUBLIC_VALIDATION_MESSAGES.INVALID_ANSWERS;
  }

  const source = trimString(input.source);
  if (!source) return ROUTING_PUBLIC_VALIDATION_MESSAGES.SOURCE_REQUIRED;
  if (source.length > 120) {
    return ROUTING_PUBLIC_VALIDATION_MESSAGES.SOURCE_TOO_LONG;
  }

  return null;
};

const buildSubmitRequest = (
  input: RoutingPublicSubmitInput,
): RoutingPublicSubmitRequest => ({
  ...(trimString(input.inviteeName)
    ? { inviteeName: trimString(input.inviteeName) }
    : {}),
  ...(trimString(input.inviteeEmail)
    ? { inviteeEmail: trimString(input.inviteeEmail) }
    : {}),
  ...(trimString(input.inviteePhone)
    ? { inviteePhone: trimString(input.inviteePhone) }
    : {}),
  answers: input.answers,
  source: trimString(input.source),
});

export const routingPublicApi = createApi({
  reducerPath: "routingPublicApi",
  baseQuery: publicBaseQuery,
  tagTypes: ["RoutingPublicForm"],
  keepUnusedDataFor: 60,
  refetchOnReconnect: true,
  endpoints: (builder) => ({
    getRoutingPublicForm: builder.query<
      RoutingPublicForm,
      GetRoutingPublicFormInput
    >({
      queryFn: async (input, _api, _extraOptions, baseQuery) => {
        const normalizedInput = normalizeFormInput(input);
        const publicKeyError = validatePublicKey(normalizedInput.publicKey);
        if (publicKeyError) return createApiError(400, publicKeyError);

        const result = await baseQuery(
          ENDPOINTS.publicByPublicKey(normalizedInput.publicKey),
        );
        if (result.error) return { error: result.error };

        const envelope = result.data as RoutingPublicFormResponse;
        if (!envelope?.success || !isRoutingPublicForm(envelope.data)) {
          return createApiError(
            500,
            extractEnvelopeMessage(
              result.data,
              ROUTING_PUBLIC_VALIDATION_MESSAGES.INVALID_RESPONSE,
            ),
          );
        }

        return { data: envelope.data };
      },
      providesTags: (_result, _error, input) => {
        const publicKey =
          typeof input === "string" ? trimString(input) : trimString(input.publicKey);
        return [{ type: "RoutingPublicForm", id: publicKey }];
      },
    }),

    submitRoutingPublicForm: builder.mutation<
      RoutingPublicSubmitResult,
      RoutingPublicSubmitInput
    >({
      queryFn: async (input, _api, _extraOptions, baseQuery) => {
        const validationError = validateSubmitInput(input);
        if (validationError) return createApiError(400, validationError);

        const result = await baseQuery({
          url: ENDPOINTS.publicSubmit(trimString(input.publicKey)),
          method: "POST",
          body: buildSubmitRequest(input),
        });
        if (result.error) return { error: result.error };

        const envelope = result.data as RoutingPublicSubmitResponse;
        if (!envelope?.success || !isRoutingPublicSubmitData(envelope.data)) {
          return createApiError(
            500,
            extractEnvelopeMessage(
              result.data,
              ROUTING_PUBLIC_VALIDATION_MESSAGES.INVALID_RESPONSE,
            ),
          );
        }

        return {
          data: {
            data: envelope.data ?? {},
            message: extractEnvelopeMessage(
              result.data,
              "Routing form submitted successfully",
            ),
          },
        };
      },
    }),
  }),
});

export const {
  useGetRoutingPublicFormQuery,
  useLazyGetRoutingPublicFormQuery,
  useSubmitRoutingPublicFormMutation,
} = routingPublicApi;
